import type { ClientMessage, PageVisibility } from '@konvoi/shared';

/**
 * Was der Client dem Server sagt, sobald eine Verbindung wieder **bestätigt**
 * ist.
 *
 * ## Neubehaupten statt Nachreichen
 *
 * Eine Verbindung, die abreißt, nimmt alles mit, was der Server über dieses
 * Gerät wusste — er vergisst es absichtlich, sonst rechnete er für Geräte
 * weiter, die niemand mehr sieht. Der Client muss seinen Zustand also erneut
 * behaupten.
 *
 * **Und zwar den von jetzt, nicht die Nachricht von damals.** Eine
 * Warteschlange, die alte Absichten nachspielt, stellt sie zu, wenn sie falsch
 * geworden sind: ein „ich bin im Hintergrund", während der Fahrer auf den
 * Schirm sieht; ein „nimm den aus der Kolonne" für jemanden, der längst wieder
 * da ist. Zustände werden neu behauptet, Ereignisse gepuffert — das sind zwei
 * verschiedene Dinge, und nur das zweite heißt Nachreichen.
 *
 * ## Warum das hier steht und nicht im Store
 *
 * Damit die Regel einen Namen hat und ohne Browser prüfbar ist. Bis zum
 * 14.09.2026 stand sie als drei Zeilen in `onReady` — und genau deshalb fiel
 * niemandem auf, dass die Sichtbarkeit fehlte: Die Navigation wurde neu
 * behauptet, die Sichtbarkeit nicht. Nach dem Funkloch hielt der Server ein
 * weggelegtes Gerät deshalb für fahrend (A-111).
 */
export interface StandBeimWiederverbinden {
  /** Liegt die App gerade im Hintergrund? */
  readonly pageVisibility: PageVisibility;
  /** Steht dem Fahrer gerade eine Rückführung vor Augen? */
  readonly navigationLaeuft: boolean;
}

export function nachrichtenBeimWiederverbinden(
  stand: StandBeimWiederverbinden,
): readonly ClientMessage[] {
  const nachrichten: ClientMessage[] = [
    /*
     * Ohne Bedingung, auch im Vordergrund.
     *
     * „Nur melden, wenn es abweicht" hieße zu wissen, was der Server annimmt —
     * und genau diese Annahme ist beim Wiederverbinden das Unsicherste, was es
     * gibt. Eine Nachricht je Wiederverbindung ist der Preis dafür, dass die
     * beiden Buchführungen nicht auseinanderlaufen können.
     */
    { type: 'visibility', state: stand.pageVisibility },
  ];

  /*
   * Eine Navigation übersteht ein Funkloch: Wer sie noch vor Augen hat, fordert
   * sie beim Wiederverbinden selbst an. Für den Fahrer bleibt sie dabei
   * ununterbrochen stehen.
   */
  if (stand.navigationLaeuft) {
    nachrichten.push({ type: 'navigation_start' });
  }

  return nachrichten;
}
